// 로봇 상세 공통 부품(2026-11-02 오너 — 명판 A안 "a안으로 하고, 출고이력, 제조 이력, 이런것들도 … 개요 페이지랑
// 비슷한 테마로"). 개요와 모든 탭이 같은 판 · 머리 · 정보 줄을 쓴다.
//   · 판(Sheet) — 흰 바탕 한 장. 안은 가는 선으로 나눈다(카드를 여러 장 쌓지 않는다).
//   · 정보 줄(Fact) — 이름표는 옅고 작게, 값은 진하고 크게. 빈 값은 '없음'.
// 모양은 globals.css 의 rd-* 규칙에 있다.

import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { ROBOT_LIFECYCLE_STAGES, type RobotLifecycleStage } from '@/components/StatusBar';
import { ROBOT_STAGE_META } from '@/components/LifecyclePipeline';

export function Sheet({ className, children, ...rest }: HTMLAttributes<HTMLElement>) {
  return (
    <section className={className ? `rd-sheet ${className}` : 'rd-sheet'} {...rest}>
      {children}
    </section>
  );
}

/** 판 머리 — 이름 · 건수 · (오른쪽) 동작. */
export function SheetHead({
  title,
  count,
  note,
  actions,
}: {
  title: ReactNode;
  count?: number;
  note?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="rd-sheet-head">
      <h2>{title}</h2>
      {count != null && <span className="rd-count">{count}</span>}
      {note}
      {actions && <div className="rd-head-actions">{actions}</div>}
    </header>
  );
}

/** 판 안 묶음(식별 정보 · 소유 · 위치 …). 묶음끼리는 가는 선으로 나뉜다. */
export function FactSection({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="rd-fact">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

export function FactList({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <dl className="rd-dl" style={style}>
      {children}
    </dl>
  );
}

/** 이름표 · 값 한 줄. 값이 비면(null · undefined · '') '없음'. */
export function Fact({ k, v, mono }: { k: ReactNode; v: ReactNode; mono?: boolean }) {
  const empty = v == null || v === '' || v === '–' || v === '-';
  return (
    <div className="rd-kv">
      <dt>{k}</dt>
      <dd className={mono && !empty ? 'rd-mono' : undefined}>{empty ? <Empty /> : v}</dd>
    </div>
  );
}

export function Empty({ text = '없음' }: { text?: string }) {
  return <span className="rd-empty">{text}</span>;
}

/** 판 안이 비었을 때 — 무엇이 없는지와 다음 할 일을 한 문장으로. */
export function SheetEmpty({ children }: { children: ReactNode }) {
  return <div className="rd-sheet-empty">{children}</div>;
}

/** 판 안의 표 — 가로로 넘치면 이 상자 안에서만 밀린다. 표 모양은 콘솔 공통 규칙을 따른다. */
export function SheetTable({ children }: { children: ReactNode }) {
  return <div className="rd-table">{children}</div>;
}

// ── 이력 목록 ─────────────────────────────────────────────
// 날짜(두 줄) · 동그라미(연한 세로선으로 잇는다, 2026-11-02 오너) · 내용. 개요의 최근 이력과 로더 교체 이력이 같이 쓴다.
export interface HistoryItem {
  id: string;
  date: string;
  time?: string;
  /** 동그라미 색(CSS 값) */
  color: string;
  title: ReactNode;
  /** 내용 아래 작은 꼬리표(종류) */
  tag?: string;
  /** 꼬리표 옆 한 줄(누가 · 사유) */
  meta?: ReactNode;
  /** 이력 편집 — 줄을 눌러 고른다(그 시점으로 되돌리기). pickLabel 은 스크린리더용. */
  onPick?: () => void;
  pickLabel?: string;
  /** 줄 오른쪽 끝(편집 모드의 꼬리표 · 버튼) */
  actions?: ReactNode;
  /** 숨긴 줄 — 줄을 긋고 옅게 */
  muted?: boolean;
}

export function HistoryList({ items }: { items: HistoryItem[] }) {
  return (
    <ol className="rd-tl">
      {items.map((e) => {
        const main = (
          <>
            <div className="rd-tl-title">{e.title}</div>
            {(e.tag || e.meta) && (
              <div className="rd-tl-meta">
                {e.tag && <span className="rd-tl-kind">{e.tag}</span>}
                {e.meta}
              </div>
            )}
          </>
        );
        return (
          <li
            key={e.id}
            className={e.muted ? 'rd-tl-row is-hidden' : 'rd-tl-row'}
            data-testid={`timeline-row-${e.id}`}
          >
            <div className="rd-tl-when">
              {e.date}
              {e.time && <span>{e.time}</span>}
            </div>
            <span className="rd-tl-dot" style={{ '--dot': e.color } as CSSProperties} aria-hidden />
            {e.onPick ? (
              <button
                type="button"
                className="rd-tl-pick"
                onClick={e.onPick}
                aria-label={e.pickLabel}
              >
                {main}
              </button>
            ) : (
              <div style={{ minWidth: 0 }}>{main}</div>
            )}
            {e.actions && <div className="rd-tl-actions">{e.actions}</div>}
          </li>
        );
      })}
    </ol>
  );
}

// ── 라이프사이클 띠 ─────────────────────────────────────────────
// 아홉 칸을 한 줄로 이어 그리면 '운영중 → 기타 → 정비' 처럼 순서가 있는 것으로 읽혔다(2026-11-02 오너 화면).
// 기본 흐름 네 칸만 막대로 잇고, 나머지 다섯 상태는 '흐름 밖'으로 따로 둔다.
const MAIN_PATH: RobotLifecycleStage[] = ['stock', 'shipping', 'installing', 'operating'];
const OFF_PATH = ROBOT_LIFECYCLE_STAGES.filter((s) => !MAIN_PATH.includes(s));

export function LifecycleTrack({ current }: { current: RobotLifecycleStage }) {
  const idx = MAIN_PATH.indexOf(current);
  const meta = ROBOT_STAGE_META[current];
  return (
    <div
      className="rd-track"
      role="group"
      aria-label={`라이프사이클 — 지금 ${meta?.label ?? ''}`}
      style={{ '--sc': meta?.color, '--st': meta?.tint } as CSSProperties}
    >
      <ol className="rd-track-main">
        {MAIN_PATH.map((s, j) => {
          const cls = idx < 0 ? '' : j < idx ? ' done' : j === idx ? ' now' : '';
          return (
            <li key={s} className={`rd-seg${cls}`} aria-current={j === idx ? 'step' : undefined}>
              <span className="rd-seg-bar" />
              <span className="rd-seg-label">{ROBOT_STAGE_META[s].label}</span>
            </li>
          );
        })}
      </ol>
      <div className="rd-track-side">
        <span className="rd-track-cap">흐름 밖</span>
        {OFF_PATH.map((s) => (
          <span
            key={s}
            className={s === current ? 'rd-chip now' : 'rd-chip'}
            aria-current={s === current ? 'step' : undefined}
          >
            {ROBOT_STAGE_META[s].label}
          </span>
        ))}
      </div>
    </div>
  );
}
